import type {
  ProfileTemplateFieldMode,
  ProfileTemplateFieldPoliciesDto,
} from '@up2stay/up2promo-api-client';
import { graphemeLength } from '@up2stay/up2promo-commons';
import { Label, Switch } from '@up2stay/up2promo-ui';

import { TemplateFieldRow } from '@/core/components/profile-templates/template-field-row';
import type { TemplateMemberValuesEditorUiProps } from '@/core/components/profile-templates/template-policy-member-controls';
import {
  TEMPLATE_DETAIL_MUTED_CLASS,
  TEMPLATE_DETAIL_SECTION_TITLE_CLASS,
  TemplateDetailSectionCard,
} from '@/core/components/profile-templates/template-detail-section-card';
import { InputWithCharCount } from '@/core/components/ui/field-with-char-count';
import type { ProfileTemplateFieldKey } from '@/core/lib/profile-template-fields';
import type { TemplateFormData } from '@/core/lib/profile-template-form';

const MAX_JOB_TITLE = 30;
const MAX_COMPANY = 30;
const MAX_LOCATION = 30;

export type TemplateDetailBasicsDict = {
  title: string;
  description: string;
  jobTitle: string;
  jobTitlePlaceholder: string;
  company: string;
  companyPlaceholder: string;
  location: string;
  locationPlaceholder: string;
  showProfileImage: string;
  showProfileImageDescription: string;
};

export type TemplateDetailBasicsFieldLabels = {
  jobTitle: string;
  company: string;
  location: string;
  showProfileImage: string;
};

type TemplateDetailBasicsSectionProps = {
  dict: TemplateDetailBasicsDict;
  fieldLabels: TemplateDetailBasicsFieldLabels;
  policies: ProfileTemplateFieldPoliciesDto;
  onPolicyChange: (
    key: ProfileTemplateFieldKey,
    mode: ProfileTemplateFieldMode,
  ) => void;
  canEditTemplate: boolean;
  policyLabels: { locked: string; free: string };
  memberValueProps: TemplateMemberValuesEditorUiProps;
  watchedJobTitle: string | null | undefined;
  watchedCompany: string | null | undefined;
  watchedLocation: string | null | undefined;
  watchedForm: TemplateFormData;
  disabled: boolean;
  onFieldChange: <K extends keyof TemplateFormData>(
    key: K,
    value: TemplateFormData[K],
  ) => void;
};

export function TemplateDetailBasicsSection({
  dict,
  fieldLabels,
  policies,
  onPolicyChange,
  canEditTemplate,
  policyLabels,
  memberValueProps,
  watchedJobTitle,
  watchedCompany,
  watchedLocation,
  watchedForm,
  disabled,
  onFieldChange,
}: TemplateDetailBasicsSectionProps) {
  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <TemplateDetailSectionCard contentClassName="gap-4">
        <div className="flex flex-col gap-1">
          <h2 className={TEMPLATE_DETAIL_SECTION_TITLE_CLASS}>{dict.title}</h2>
          <p className={TEMPLATE_DETAIL_MUTED_CLASS}>{dict.description}</p>
        </div>
        <div className="flex min-w-0 flex-col gap-3">
          <TemplateFieldRow
            fieldKey="jobTitle"
            policies={policies}
            onPolicyChange={onPolicyChange}
            canManage={canEditTemplate}
            policyLabels={policyLabels}
            memberInputMaxLength={MAX_JOB_TITLE}
            memberInputPlaceholder={dict.jobTitlePlaceholder}
            memberValueColumn={{
              kind: 'field',
              key: 'jobTitle',
              label: fieldLabels.jobTitle,
            }}
            memberDialogTitle={fieldLabels.jobTitle}
            memberDefaultValue={String(watchedJobTitle ?? '')}
            {...memberValueProps}
            label={<Label htmlFor="jobTitle">{dict.jobTitle}</Label>}
          >
            <InputWithCharCount
              id="jobTitle"
              placeholder={dict.jobTitlePlaceholder}
              className="min-w-0 w-full"
              maxLength={MAX_JOB_TITLE}
              valueLength={graphemeLength(String(watchedJobTitle ?? ''))}
              value={watchedJobTitle ?? ''}
              disabled={disabled}
              onChange={(event) => {
                onFieldChange('jobTitle', event.target.value);
              }}
            />
          </TemplateFieldRow>

          <TemplateFieldRow
            fieldKey="company"
            policies={policies}
            onPolicyChange={onPolicyChange}
            canManage={canEditTemplate}
            policyLabels={policyLabels}
            memberInputMaxLength={MAX_COMPANY}
            memberInputPlaceholder={dict.companyPlaceholder}
            memberValueColumn={{
              kind: 'field',
              key: 'company',
              label: fieldLabels.company,
            }}
            memberDialogTitle={fieldLabels.company}
            memberDefaultValue={String(watchedCompany ?? '')}
            {...memberValueProps}
            label={<Label htmlFor="company">{dict.company}</Label>}
          >
            <InputWithCharCount
              id="company"
              placeholder={dict.companyPlaceholder}
              className="min-w-0 w-full"
              maxLength={MAX_COMPANY}
              valueLength={graphemeLength(String(watchedCompany ?? ''))}
              value={watchedCompany ?? ''}
              disabled={disabled}
              onChange={(event) => {
                onFieldChange('company', event.target.value);
              }}
            />
          </TemplateFieldRow>

          <TemplateFieldRow
            fieldKey="location"
            policies={policies}
            onPolicyChange={onPolicyChange}
            canManage={canEditTemplate}
            policyLabels={policyLabels}
            memberInputMaxLength={MAX_LOCATION}
            memberInputPlaceholder={dict.locationPlaceholder}
            memberValueColumn={{
              kind: 'field',
              key: 'location',
              label: fieldLabels.location,
            }}
            memberDialogTitle={fieldLabels.location}
            memberDefaultValue={String(watchedLocation ?? '')}
            {...memberValueProps}
            label={<Label htmlFor="location">{dict.location}</Label>}
          >
            <InputWithCharCount
              id="location"
              placeholder={dict.locationPlaceholder}
              className="min-w-0 w-full"
              maxLength={MAX_LOCATION}
              valueLength={graphemeLength(String(watchedLocation ?? ''))}
              value={watchedLocation ?? ''}
              disabled={disabled}
              onChange={(event) => {
                onFieldChange('location', event.target.value);
              }}
            />
          </TemplateFieldRow>

          <TemplateFieldRow
            fieldKey="showProfileImage"
            policies={policies}
            onPolicyChange={onPolicyChange}
            canManage={canEditTemplate}
            policyLabels={policyLabels}
            memberValueColumn={{
              kind: 'setting',
              key: 'showProfileImage',
              label: fieldLabels.showProfileImage,
              inputType: 'boolean',
            }}
            memberDialogTitle={fieldLabels.showProfileImage}
            memberDefaultValue={
              (watchedForm.showProfileImage ?? true) ? 'true' : 'false'
            }
            {...memberValueProps}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-0.5">
                <Label htmlFor="showProfileImage">
                  {dict.showProfileImage}
                </Label>
                <p className={TEMPLATE_DETAIL_MUTED_CLASS}>
                  {dict.showProfileImageDescription}
                </p>
              </div>
              <Switch
                id="showProfileImage"
                checked={watchedForm.showProfileImage ?? true}
                disabled={disabled}
                onCheckedChange={(checked) => {
                  onFieldChange('showProfileImage', checked);
                }}
              />
            </div>
          </TemplateFieldRow>
        </div>
      </TemplateDetailSectionCard>
    </div>
  );
}
